import { Container } from 'pixi.js-legacy';

/**
 * PixiJS Container layer hierarchy
 *
 * Render order (bottom to top):
 * 1. background - Gradient background
 * 2. speedLines - Background patterns (stripe, diamond, etc.)
 * 3. playAreaBorder - Play area border
 * 4. gameWorld - Game entities (affected by screen shake)
 *    - bullets
 *    - oneWayBullets
 *    - lasers
 *    - bosses
 *    - items
 *    - particles
 *    - player
 *    - effects (boss death, etc.)
 * 5. ui - UI elements (not affected by screen shake)
 */
export class PixiLayers {
    // Background layers
    public background: Container;
    public speedLines: Container;
    public playAreaBorder: Container;

    // Game world container (for screen shake)
    public gameWorld: Container;

    // Entity layers (inside gameWorld)
    public bullets: Container;
    public oneWayBullets: Container;
    public lasers: Container;
    public bosses: Container;
    public items: Container;
    public particles: Container;
    public player: Container;
    public effects: Container;

    // UI layer (outside gameWorld, not affected by shake)
    public ui: Container;

    constructor(stage: Container) {
        // Create layers in render order (bottom to top)
        // v7: label 속성 대신 name 속성 사용

        // Background layers
        this.background = new Container();
        this.background.name = 'background';
        stage.addChild(this.background);

        this.speedLines = new Container();
        this.speedLines.name = 'speedLines';
        stage.addChild(this.speedLines);

        this.playAreaBorder = new Container();
        this.playAreaBorder.name = 'playAreaBorder';
        stage.addChild(this.playAreaBorder);

        // Game world container (will be translated for screen shake)
        this.gameWorld = new Container();
        this.gameWorld.name = 'gameWorld';
        stage.addChild(this.gameWorld);

        // Entity layers inside gameWorld
        this.bullets = new Container();
        this.bullets.name = 'bullets';
        this.gameWorld.addChild(this.bullets);

        this.oneWayBullets = new Container();
        this.oneWayBullets.name = 'oneWayBullets';
        this.gameWorld.addChild(this.oneWayBullets);

        this.lasers = new Container();
        this.lasers.name = 'lasers';
        this.gameWorld.addChild(this.lasers);

        this.bosses = new Container();
        this.bosses.name = 'bosses';
        this.gameWorld.addChild(this.bosses);

        this.items = new Container();
        this.items.name = 'items';
        this.gameWorld.addChild(this.items);

        this.particles = new Container();
        this.particles.name = 'particles';
        this.gameWorld.addChild(this.particles);

        this.player = new Container();
        this.player.name = 'player';
        this.gameWorld.addChild(this.player);

        this.effects = new Container();
        this.effects.name = 'effects';
        this.gameWorld.addChild(this.effects);

        // UI layer (not affected by screen shake)
        this.ui = new Container();
        this.ui.name = 'ui';
        stage.addChild(this.ui);
    }

    /**
     * Apply screen shake offset to gameWorld
     */
    applyScreenShake(offsetX: number, offsetY: number): void {
        this.gameWorld.position.set(offsetX, offsetY);
    }

    /**
     * Reset screen shake
     */
    resetScreenShake(): void {
        this.gameWorld.position.set(0, 0);
    }

    /**
     * Clear all entity layers (for game reset)
     * 자식 객체들을 destroy하여 메모리 누수 방지
     */
    clearEntities(): void {
        this.destroyChildren(this.bullets);
        this.destroyChildren(this.oneWayBullets);
        this.destroyChildren(this.lasers);
        this.destroyChildren(this.bosses);
        this.destroyChildren(this.items);
        this.destroyChildren(this.particles);
        this.destroyChildren(this.player);
        this.destroyChildren(this.effects);
    }

    /**
     * Clear all layers including background
     * 자식 객체들을 destroy하여 메모리 누수 방지
     */
    clearAll(): void {
        this.destroyChildren(this.background);
        this.destroyChildren(this.speedLines);
        this.destroyChildren(this.playAreaBorder);
        this.clearEntities();
        this.destroyChildren(this.ui);
    }

    /**
     * 컨테이너의 모든 자식을 제거하고 destroy
     */
    private destroyChildren(container: Container): void {
        const children = container.removeChildren();
        for (const child of children) {
            child.destroy();
        }
    }

    /**
     * Get a specific layer by name
     */
    getLayer(name: string): Container {
        switch (name) {
            case 'background': return this.background;
            case 'speedLines': return this.speedLines;
            case 'playAreaBorder': return this.playAreaBorder;
            case 'gameWorld': return this.gameWorld;
            case 'bullets': return this.bullets;
            case 'oneWayBullets': return this.oneWayBullets;
            case 'lasers': return this.lasers;
            case 'bosses': return this.bosses;
            case 'items': return this.items;
            case 'particles': return this.particles;
            case 'player': return this.player;
            case 'effects': return this.effects;
            case 'ui': return this.ui;
            default:
                throw new Error(`Unknown layer: ${name}`);
        }
    }
}
